import { Container } from '@material-ui/core';
import { Formik, FormikErrors, FormikHelpers, FormikTouched, useField } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { getApiEmployerProductFunds, postKorpoCode } from '../../api/korpoApi';
import { ApiKorpoCodeResponseStatus, validKorpoCodeResponseStatus } from '../../api/model/ApiKorpoCodeResponse';
import ProductWizardState from '../../model/ProductWizardState';
import { cartStateSlice, productWizardActions } from '../../model/store';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import Screen from '../ProductWizard/Screen';
import * as Yup from 'yup';
import { ProductType, KorpoEmployerFundData, UnitCategoryCode, RegisterMode } from 'cart-model';
import {
  FINANCIAL_INSTITUTION_MIN_LENGTH,
  FINANCIAL_INSTITUTION_REGEX,
  FINANCIAL_INSTITUTION_MAX_LENGTH } from '../OpenIkeIkzeKorpo/OpenIkeIkzeKorpo';
import {
  Label,
  Radio,
  Button,
  TextField,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Typography,
  Card
} from 'ui-components';
import { useRouter } from 'common-react/src/router/router';
import { CustomForm } from '../formik/CustomForm/CustomForm';
import { useUniqueWcagValues } from 'common-react';
import _ from 'lodash';

import useStyles from './TransferIkeIkze.style';

export enum TransferIkeIkzeOperationType {
  transfer = 'transfer',
  ppe_transfer = 'ppe_transfer',
  korpo_transfer = 'korpo_transfer' }

export interface TransferIkeIkzeProcessData {
  operationType: TransferIkeIkzeOperationType;
  disclaimer?: boolean;
  financialInstitution?: string;
  korpoCode?: string;
}

const TransferIkeIkze: React.FC = () => {
  const c = useStyles({});
  const [t] = useTranslation('transfer-ike-ikze');
  const [tt] = useTranslation('translation');
  const dispatch = useDispatch();
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const allFunds = useSelector((state: ProductWizardState) => state.apiState.funds);
  const [url] = useTranslation('url');
  const { history } = useRouter();
  const { getUniqueValue } = useUniqueWcagValues();
  const transferIkeIkzeLabelId = getUniqueValue('transfer-ike-ikze-label');
  const transferIkeIkzeRadioButtonId = getUniqueValue('transfer-ike-ikze-radio-button');
  const transferIkeIkzeRadioGroup = getUniqueValue('transfer-ike-ikze-radio-group');
  const transferIkeIkzeKorpoTransferRadioButton = getUniqueValue('transfer-ike-ikze-korpo-transfer-radio-button');
  const transferIkeIkzePpeTransferRadioButton = getUniqueValue('transfer-ike-ikze-ppe-transfer-radio-button');
  if (productType === undefined || productType === ProductType.funds) {
    return <Redirect to="/"/>;
  }

  const validationSchema = Yup.object<TransferIkeIkzeProcessData>().shape({
    operationType: Yup.mixed<TransferIkeIkzeOperationType>()
                      .oneOf(Object.values(TransferIkeIkzeOperationType), t('validation.at-least-one-option-required'))
                      .required(t('validation.at-least-one-option-required')),
    disclaimer: Yup.boolean().when('operationType', {
      is: (v => ['transfer', 'korpo_transfer'].includes(v)),
      then: Yup.boolean().equals([true], t('validation.required')),
      otherwise: Yup.boolean().notRequired() }),
    financialInstitution: Yup.string().when('operationType', {
      is: (v => ['transfer', 'korpo_transfer'].includes(v)),
      then: Yup.string().required().trim()
          .min(FINANCIAL_INSTITUTION_MIN_LENGTH)
          .max(FINANCIAL_INSTITUTION_MAX_LENGTH)
          .matches(FINANCIAL_INSTITUTION_REGEX),
      otherwise: Yup.string().trim().notRequired() }),
    korpoCode: Yup.string().when('operationType', {
      is: 'korpo_transfer',
      then: Yup.string().trim().required().min(3),
      otherwise: Yup.string().trim().notRequired() }) });

  const initialValues = {
    operationType: 'transfer' as TransferIkeIkzeOperationType,
    disclaimer: false,
    financialInstitution: '',
    korpoCode: '' };

  const FinancialInstitutionBox = ({ disabled }: { disabled: boolean }) => {
    const [disclaimerField, disclaimerMeta] = useField('disclaimer');
    const [institutionField, institutionMeta] = useField('financialInstitution');
    const { getUniqueValue } = useUniqueWcagValues();
    const disclaimerId = getUniqueValue('transfer-ike-ikze-disclaimer-checkbox');

    return (
      <>
        <FormControlLabel
          className={c.disclaimerFormControl}
          htmlFor={disclaimerId}
          label={t(`${productType.toLowerCase()}.disclaimer`)}
          control={
            <Checkbox
              data-testid="disclaimer"
              id={disclaimerId}
              disabled={disabled}
              required={true}
              checked={Boolean(disclaimerField.value)}
              hasError={disclaimerMeta.touched && !!disclaimerMeta.error}
              inputProps={{
                'aria-required': true
              }}
              {...disclaimerField}
            />
          }
        />
        <TextField
          className={c.financialInstitutionInput}
          testId="financialInstitution"
          label={t('ike-ikze.financial-institution.label')}
          disabled={disabled}
          required={true}
          hasError={institutionMeta.touched && !!institutionMeta.error}
          error={institutionMeta.touched && institutionMeta.error}
          placeholder={t('ike-ikze.financial-institution.placeholder')}
          {...institutionField}
        />
      </>
    );
  };
  const KorpoCodeBox = ({ disabled }: { disabled: boolean }) => {
    const [korpoCodeField, korpoCodeMeta] = useField('korpoCode');

    return (
      <div className={c.korpoCodeBox}>
        <Typography>
          {t('ike-ikze.korpo-code.title')}
        </Typography>
        <TextField
          className={c.korpoCodeInput}
          testId="korpoCode"
          label={t('ike-ikze.korpo-code.label')}
          disabled={disabled}
          required={true}
          hasError={korpoCodeMeta.touched && !!korpoCodeMeta.error}
          error={korpoCodeMeta.touched && korpoCodeMeta.error}
          placeholder={t('ike-ikze.korpo-code.placeholder')}
          {...korpoCodeField}
        />
      </div>
    );
  };

  const processKorpoCodeError = (formikHelpers: FormikHelpers<TransferIkeIkzeProcessData>, status: ApiKorpoCodeResponseStatus) => {
    formikHelpers.setFieldError('korpoCode', status === 'USED'
        ? t('ike-ikze.korpo-code.code-used')
        : t('ike-ikze.korpo-code.code-error'));
    formikHelpers.setSubmitting(false);
  };

  const processKorpoTransfer = (values: TransferIkeIkzeProcessData,
                                formikHelpers: FormikHelpers<TransferIkeIkzeProcessData>) => {
    const korpoCode = values.korpoCode || '';
    postKorpoCode(productType === ProductType.ike ? 'IKEK' : 'IKZEK', korpoCode)
        .then(response => {
          if (!validKorpoCodeResponseStatus.includes(response.status)) {
            processKorpoCodeError(formikHelpers, response.status);
            return;
          }
          dispatch(cartStateSlice.actions.clearCart());
          getApiEmployerProductFunds(response.productIdn, response.employerIdn).then(fundsResponse => {
            const unitCategoryCode = fundsResponse[0].shareClassIdn as UnitCategoryCode;
            dispatch(productWizardActions.setKorpoData({
              unitCategoryCode,
              korpoCode,
              productCode: productType,
              productIdn: response.productIdn,
              employerIdn: response.employerIdn,
              allowedFunds: fundsResponse.map(fundResponse => {
                const foundFund = allFunds.find(fund => fund.afsCodeForUnitCode[unitCategoryCode] === fundResponse.idn);
                return {
                  fundId: foundFund ? foundFund.id : undefined,
                  fundIdn: fundResponse.idn,
                  minPurchase: fundResponse.minPurchase,
                  minPurchaseOpening: fundResponse.minPurchaseOpening } as KorpoEmployerFundData;
              }),
              statementPl: response.statementPl,
              statementEn: response.statementEng }));
            dispatch(cartStateSlice.actions.setTransferInstitutionName(values.financialInstitution));
            dispatchRegisterMode(values.operationType);

            const nextUrl = `/${url('product-wizard')}/${url('select-funds')}`;
            history.push(nextUrl);
          }).catch(() => {
            processKorpoCodeError(formikHelpers, 'ERR');
          });
        })
        .catch(() => {
          processKorpoCodeError(formikHelpers, 'ERR');
        });
  };

  const dispatchRegisterMode = (operationType: TransferIkeIkzeOperationType) => {
    switch (operationType) {
      case 'transfer':
        dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.TRANSFER));
        break;
      case 'ppe_transfer':
        dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.PPE_TRANSFER_PAYMENT));
        break;
      case 'korpo_transfer':
        dispatch(cartStateSlice.actions.setRegisterMode(RegisterMode.EXT_TRANSFER_KORPO));
        break;
      default:
        throw new Error();
    }
  };

  const onSubmitTransferTypeForm = (values: TransferIkeIkzeProcessData,
                                    formikHelpers: FormikHelpers<TransferIkeIkzeProcessData>): void => {
    if (values.operationType === 'korpo_transfer') {
      processKorpoTransfer(values, formikHelpers);
    } else {
      dispatch(cartStateSlice.actions.clearCart());
      dispatch(cartStateSlice.actions.setTransferInstitutionName(values.operationType === 'transfer' ? values.financialInstitution : undefined));
      dispatchRegisterMode(values.operationType);
      const nextUrl = `/${url('product-wizard')}/${url('select-funds')}`;
      history.push(nextUrl);
    }
  };

  const hasError = (fieldName: string, checked: boolean, touched: FormikTouched<TransferIkeIkzeProcessData>, errors: FormikErrors<TransferIkeIkzeProcessData>) => {
    return !checked && _.get(touched, fieldName) && _.get(errors, fieldName);
  };

  return (
    <div className={c.wrapper}>
      <ProductWizardTitle
        titleKey={
          productType === ProductType.ike
          ? Screen.TRANSFER_IKE.code
          : Screen.TRANSFER_IKZE.code
        }
      />
      <Card className={c.root} fullWidth>
        <Label
          size="xl"
          fontWeight='bold'
          color="dark"
          hasBottomMargin
          id={transferIkeIkzeLabelId}
          className={c.label}
        >
          {t('ike-ikze.type-label')}
        </Label>
        <Formik<TransferIkeIkzeProcessData>
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={onSubmitTransferTypeForm}
        >
          {({ isSubmitting, values, touched, errors, setFieldValue, isValid }) => {
            const handleRadioGroupChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
              setFieldValue('operationType', value);
            };

            const isFormComplete = () => {
              if (!values.operationType) {
                return false;
              }
              if (['transfer', 'korpo_transfer'].includes(values.operationType)) {
                if (!values.disclaimer || !values.financialInstitution?.trim()) {
                  return false;
                }
              }
              if (values.operationType === 'korpo_transfer') {
                if (!values.korpoCode?.trim()) {
                  return false;
                }
              }
              return true;
            };

            const isButtonDisabled = isSubmitting || !isValid || !isFormComplete();

            return (
              <CustomForm autoComplete="off">
                <RadioGroup
                  className={c.radioGroup}
                  name="operationType"
                  id={transferIkeIkzeRadioGroup}
                  required={true}
                  ariaRequiredId={transferIkeIkzeRadioGroup}
                  aria-labelledby={transferIkeIkzeLabelId}
                  value={values.operationType}
                  onChange={handleRadioGroupChange}
                >
                  <FormControlLabel className={c.radioButtonLabel}
                                    value={'transfer'}
                                    control={<Radio id={transferIkeIkzeRadioButtonId} hasError={hasError('operationType', !!values.operationType, touched, errors)} />}
                                    data-testid="transfer"
                                    disabled={isSubmitting}
                                    htmlFor={transferIkeIkzeRadioButtonId}
                                    label={t(`${productType.toLowerCase()}.radio-button.transfer`)}/>
                  {values.operationType === 'transfer' &&
                    <div className={c.radioOptionDetailsBox}>
                      <FinancialInstitutionBox disabled={isSubmitting}/>
                    </div>}
                  <FormControlLabel className={c.radioButtonLabel}
                                    value={'korpo_transfer'}
                                    control={<Radio id={transferIkeIkzeKorpoTransferRadioButton} hasError={hasError('operationType', !!values.operationType, touched, errors)} />}
                                    data-testid="korpo_transfer"
                                    disabled={isSubmitting}
                                    htmlFor={transferIkeIkzeKorpoTransferRadioButton}
                                    label={t(`${productType.toLowerCase()}.radio-button.korpo-transfer`)}/>
                  {values.operationType === 'korpo_transfer' &&
                      <div className={c.radioOptionDetailsBox}>
                        <KorpoCodeBox disabled={isSubmitting}/>
                        <FinancialInstitutionBox disabled={isSubmitting}/>
                      </div>}
                  {productType === ProductType.ike &&
                    <FormControlLabel className={c.radioButtonLabel}
                                      value={'ppe_transfer'}
                                      control={<Radio id={transferIkeIkzePpeTransferRadioButton} hasError={hasError('operationType', !!values.operationType, touched, errors)} />}
                                      data-testid="ppe_transfer"
                                      disabled={isSubmitting}
                                      htmlFor={transferIkeIkzePpeTransferRadioButton}
                                      label={t('ike-ikze.radio-button.ppe_transfer')}/>}
                </RadioGroup>
                <div className={c.buttonContainer}>
                  <Button
                    buttonType='primary'
                    accent={true}
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isButtonDisabled}
                    fullWidth={false}
                  >
                    {tt('next')}
                  </Button>
                  <Button
                    buttonType='secondary'
                    onClick={() => history.goBack()}
                    width="auto"
                    fullWidth={false}
                    >
                    {tt('cancel')}
                  </Button>
                </div>
              </CustomForm>
            );
          }}
        </Formik>
      </Card>
    </div>
  );
};

export default TransferIkeIkze;
